import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Cpu } from 'lucide-react';

interface ModelRow {
  model_name: string;
  count: number;
}

interface ModelSelectionCardProps {
  orgId: string;
}

export function ModelSelectionCard({ orgId }: ModelSelectionCardProps) {
  const [data, setData] = useState<ModelRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const { data: rows, error } = await supabase.rpc(
        'get_org_model_selection_distribution' as any,
        { _org_id: orgId }
      ) as { data: ModelRow[] | null; error: any };

      if (!error && rows) setData(rows);
      setLoading(false);
    };
    fetch();
  }, [orgId]);

  const skipped = data.find((row) => row.model_name === 'Skipped');
  const selectedRows = data.filter((row) => row.model_name !== 'Skipped');
  const selectedTotal = selectedRows.reduce((s, r) => s + r.count, 0);
  const allTotal = data.reduce((s, r) => s + r.count, 0);

  return (
    <Card className="bg-card/80 backdrop-blur-sm border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Cpu className="h-5 w-5" />
          Model Preferences
        </CardTitle>
        <CardDescription>Tools selected in Prompt Builder</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-3/4" />
          </div>
        ) : data.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No model selections yet
          </p>
        ) : (
          <div className="space-y-3">
            {selectedRows.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No selected tools yet
              </p>
            ) : null}
            {selectedRows.map((row) => {
              const pct = selectedTotal > 0 ? Math.round((row.count / selectedTotal) * 100) : 0;
              return (
                <div key={row.model_name} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium truncate mr-2">
                      {row.model_name}
                    </span>
                    <span className="text-muted-foreground whitespace-nowrap">
                      {row.count} ({pct}%)
                    </span>
                  </div>
                  <Progress value={pct} className="h-2" />
                </div>
              );
            })}
            {skipped ? (
              <p className="border-t border-border/60 pt-3 text-xs text-muted-foreground">
                Skipped tool selection: {skipped.count} of {allTotal} prompt builder sessions.
              </p>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
